import React, { useState } from 'react';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Shield,
  Edit,
  Save,
  Trash2
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button/Button';
import { StatusBadge } from '../ui/table';
import { USER_ROLES } from '../../pages/users/UserReportsPage';

/**
 * UserDetailsModal Component
 * 
 * A modal for viewing and editing detailed user information
 */
export function UserDetailsModal({ 
  user, 
  isOpen, 
  onClose, 
  onUpdateUser, 
  onDeleteUser 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(user || {});

  // Reset edited user when user prop changes
  React.useEffect(() => {
    if (user) {
      setEditedUser(user);
      setIsEditing(false);
    }
  }, [user]);

  if (!user) return null;

  const getRoleConfig = (role) => {
    const configs = {
      [USER_ROLES.ADMIN]: { variant: 'success', label: 'Admin', icon: Shield },
      [USER_ROLES.MODERATOR]: { variant: 'warning', label: 'Moderator', icon: Shield },
      [USER_ROLES.USER]: { variant: 'default', label: 'User', icon: User }
    };
    return configs[role] || { variant: 'default', label: role, icon: User };
  };

  const roleConfig = getRoleConfig(user.user_role);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSave = () => {
    onUpdateUser?.(user.id, editedUser);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedUser(user);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${user.full_name}? This action cannot be undone.`)) {
      onDeleteUser?.(user.id);
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={editedUser.full_name || ''}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className="text-2xl font-bold text-gray-900 border-b border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent"
                />
              ) : (
                <h2 className="text-2xl font-bold text-gray-900">{user.full_name}</h2>
              )}
              
              <div className="flex items-center space-x-2 mt-1">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedUser.username || ''}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="text-gray-500 border-b border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent"
                    placeholder="Username"
                  />
                ) : (
                  <span className="text-gray-500">@{user.username}</span>
                )}
                <StatusBadge status={roleConfig.label} variant={roleConfig.variant} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave}>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* User Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Contact Information
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedUser.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900">{user.email}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedUser.phone_number || ''}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900">{user.phone_number}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  {isEditing ? (
                    <textarea
                      value={editedUser.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900">{user.address}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Personal Information
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedUser.date_of_birth || ''}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900">
                      {new Date(user.date_of_birth).toLocaleDateString()}
                      <span className="text-gray-500 ml-2">
                        (Age: {calculateAge(user.date_of_birth)})
                      </span>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Gender</label>
                  {isEditing ? (
                    <select
                      value={editedUser.gender || ''}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 capitalize">{user.gender}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  {isEditing ? (
                    <select
                      value={editedUser.user_role || ''}
                      onChange={(e) => handleInputChange('user_role', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={USER_ROLES.USER}>User</option>
                      <option value={USER_ROLES.MODERATOR}>Moderator</option>
                      <option value={USER_ROLES.ADMIN}>Admin</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{roleConfig.label}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <p className="text-gray-900">#{String(user.id).padStart(4, '0')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Account Status:</span>
                <span className="ml-2 text-green-600">Active</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Last Login:</span>
                <span className="ml-2 text-gray-600">2 hours ago</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Registration Date:</span>
                <span className="ml-2 text-gray-600">Jan 15, 2024</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total Trips:</span>
                <span className="ml-2 text-gray-600">24</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
